import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useMatchSocket } from "@/lib/useMatchSocket";
import { fmtTime, fmtKES } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogIn, UserPlus, Tv, Trophy, Handshake, Users2, HeartHandshake, Sparkles, Building, ArrowRight, Target, Eye, Crown, Megaphone, Check } from "lucide-react";
import { Logo, KenyaStripe } from "@/components/Logo";
import { PadelLiveScore, PadelFinalScore } from "@/components/PadelScore";
import SponsorInquiryDialog from "@/components/SponsorInquiryDialog";
import HashtagWall from "@/components/HashtagWall";
import PushSubscribeButton from "@/components/PushSubscribeButton";

const HERO_IMG = "/assets/hero-padel.jpg";
const SEASON_IMG = "/assets/season-2-action.jpg";

export default function PublicLive() {
  const [data, setData] = useState(null);
  const [site, setSite] = useState(null);

  useEffect(() => {
    const load = () => api.get("/public/live").then(({ data }) => setData(data)).catch(() => {});
    load();
    api.get("/public/site-content").then(({ data }) => setSite(data)).catch(() => {});
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;

  const live = data.matches.filter((m) => m.status === "live");
  const upcoming = data.matches.filter((m) => m.status === "scheduled");
  const finished = data.matches.filter((m) => m.status === "finished").slice(-6);

  return (
    <div className="min-h-screen bg-[#060d09]">
      <TopNav />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden" data-testid="public-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#BB0000] via-[#0a1611]/95 to-[#006B3F]/40 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060d09] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-28">
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#00A86B] font-bold">Kenya · Season 03 · Sept 10-13, 2026</div>
          <h1 className="font-anton mt-4 text-6xl md:text-8xl lg:text-9xl uppercase tracking-tighter leading-[0.82] text-white">
            Kenya&apos;s <span className="text-[#E32424]">Padel</span><br/>
            <span className="text-[#00A86B]">Revolution</span><br/>
            Starts Here.
          </h1>
          <KenyaStripe className="mt-6 w-56" />
          <p className="mt-6 text-zinc-200 max-w-2xl text-lg leading-relaxed">
            The premier franchise-based padel league in East Africa. 16 teams. 7-player rosters.
            KES 3.97M prize pool. One stage. One crown.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild data-testid="public-cta-register" className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold uppercase tracking-widest px-6 h-12">
              <Link to="/register-player"><UserPlus className="w-4 h-4 mr-2" /> Register as Player</Link>
            </Button>
            <SponsorInquiryDialog>
              <Button data-testid="public-cta-sponsor" className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest px-6 h-12">
                <Handshake className="w-4 h-4 mr-2" /> Sponsor Us
              </Button>
            </SponsorInquiryDialog>
            <Button asChild variant="outline" className="border-white/30 bg-white/5 backdrop-blur text-white font-bold uppercase tracking-widest px-6 h-12 hover:bg-white/10" data-testid="public-cta-login">
              <Link to="/admin"><LogIn className="w-4 h-4 mr-2" /> Operator Login</Link>
            </Button>
          </div>
          {/* stats strip */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            <Stat n="16" l="Franchise Teams" />
            <Stat n="3.97M" l="KES Prize Pool" />
            <Stat n="75:00" l="Match Format" />
            <Stat n="∞" l="Vibes" />
          </div>
        </div>
        <div className="kenya-stripe" />
      </section>

      {/* ===================== ABOUT ===================== */}
      <section id="public-about" className="max-w-7xl mx-auto px-6 py-16 md:py-24" data-testid="public-about">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#E32424] font-bold">About the League</div>
            <h2 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white mt-3 leading-[0.9]">
              The Heartbeat<br/>of Kenya&apos;s<br/><span className="text-[#00A86B]">Padel Movement.</span>
            </h2>
            <KenyaStripe className="mt-4 w-32" />
          </div>
          <div className="space-y-5 text-zinc-300 leading-relaxed text-[15px]">
            <p>
              Welcome to the <span className="text-white font-semibold">Mara Padel League</span>, the heartbeat of Kenya&apos;s
              padel revolution. We are the premier franchise-based padel league in East Africa, designed to elevate the
              sport, empower athletes, and deliver an unmatched experience for fans and corporate partners alike.
            </p>
            <p>
              Born out of a passion for high-performance sports and community engagement, the Mara Padel League brings
              together top-tier athletic talent, strategic team owners, and enthusiastic crowds. Through our unique
              <span className="text-[#00A86B] font-semibold"> franchise auction model</span>, corporate and individual
              team owners build competitive, mixed-gender squads that battle for prestige, glory, and substantial
              prize pools.
            </p>
          </div>
        </div>

        {/* Last Season Showcase */}
        <div className="mt-14 grid lg:grid-cols-[3fr_2fr] gap-8 items-stretch" data-testid="public-last-season">
          <div className="relative rounded-2xl overflow-hidden border-2 border-[#00A86B]/30 group">
            <img
              src={SEASON_IMG}
              alt="Mara Padel League — Season 02 action"
              className="w-full h-full object-cover min-h-[320px] md:min-h-[420px] transition-transform duration-700 group-hover:scale-105"
              data-testid="public-season-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="text-[10px] uppercase tracking-[0.4em] text-[#00A86B] font-bold">Season 02 · Recap</div>
              <div className="font-anton text-3xl md:text-4xl uppercase tracking-tighter text-white mt-1 leading-[0.9]">
                Sold-out stands.<br/>Unforgettable nights.
              </div>
            </div>
            <div className="absolute top-4 left-4 bg-[#E32424] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full">Last Season</div>
          </div>
          <Card className="card-mara p-7 flex flex-col justify-center">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#E32424] font-bold">A Movement, Not a Match</div>
            <div className="font-anton text-3xl uppercase tracking-tight text-white mt-3 leading-[0.95]">
              Season 03 raises<br/>the stakes.
            </div>
            <p className="text-zinc-300 mt-4 text-[15px] leading-relaxed">
              Bigger purses. Sharper courts. A wider broadcast. After two record-breaking seasons,
              Mara Padel returns with a televised franchise war that puts Kenya at the center of the
              East African padel map.
            </p>
            <div className="mt-5 grid grid-cols-3 gap-3 text-center">
              <Stat n="16" l="Teams" />
              <Stat n="64+" l="Players" />
              <Stat n="3.97M" l="KES Pool" />
            </div>
          </Card>
        </div>

        {/* Mission / Vision */}
        <div className="mt-16 grid md:grid-cols-2 gap-6">
          <Card className="card-mara p-7" data-testid="public-mission">
            <Target className="w-7 h-7 text-[#00A86B]" />
            <div className="font-anton text-2xl uppercase tracking-tight text-white mt-3">Our Mission</div>
            <p className="text-zinc-300 mt-3 text-[15px] leading-relaxed">
              To foster a vibrant, inclusive padel community in Kenya by providing a professional, world-class league
              structure that nurtures local talent and positions the country as a hub for international sports tourism.
            </p>
          </Card>
          <Card className="card-mara-red p-7" data-testid="public-vision">
            <Eye className="w-7 h-7 text-[#E32424]" />
            <div className="font-anton text-2xl uppercase tracking-tight text-white mt-3">Our Vision</div>
            <p className="text-zinc-300 mt-3 text-[15px] leading-relaxed">
              To transform padel from a fast-growing trend into a sustainable national movement, expanding pathways for
              the next generation of players and bridging the gap to global competition.
            </p>
          </Card>
        </div>

        {/* What Defines Us */}
        <div className="mt-16">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#E32424] font-bold">⚡ What Defines Us</div>
          <h3 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter text-white mt-2">Four Pillars.<br/><span className="text-[#00A86B]">One Movement.</span></h3>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Pillar n="01" icon={Trophy} title="The Franchise Model" body="A thrilling competitive structure featuring dedicated team owners, player auctions, and highly balanced mixed rosters." />
            <Pillar n="02" icon={Sparkles} title="Community & Vibe" body="An electric, family-friendly atmosphere where networking meets non-stop court-side energy." />
            <Pillar n="03" icon={Users2} title="Elite Talent Development" body="Raising the level of local play by offering players a true platform to push their game to the next level." />
            <Pillar n="04" icon={Building} title="Strategic Partnerships" body="Uniting forward-thinking brands, media partners, and premier sports facilities like Pro Padel Kenya and the Premier Club Nairobi." />
          </div>
        </div>

        {/* ===== OWN A FRANCHISE ===== */}
        <div id="public-own-franchise" className="mt-20 rounded-2xl overflow-hidden relative border border-[#E32424]/30" data-testid="public-own-franchise">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1611] via-[#1a0606] to-[#BB0000]" />
          <div className="relative p-8 md:p-12 grid lg:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#E32424] font-bold">
                <Crown className="w-3.5 h-3.5" /> Franchise Ownership · Limited Slots
              </div>
              <h3 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter text-white mt-2 leading-[0.95]">
                Own a <span className="text-[#E32424]">Mara Padel</span> Franchise.
              </h3>
              <p className="mt-4 text-zinc-300 text-[15px] leading-relaxed max-w-2xl">
                Become a Team Owner in Kenya&apos;s most ambitious franchise league. Build your roster live
                from the auction pool, captain your brand on national broadcast, and compete for the
                KES 3.97M prize pool. Only 16 franchises available across Season 03.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-zinc-200">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#E32424] shrink-0" /> Pick your 7-player squad via live auction</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#E32424] shrink-0" /> Permanent franchise rights for future seasons</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#E32424] shrink-0" /> Team jersey, branding & social packages</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#E32424] shrink-0" /> Share of prize-pool earnings + visibility</li>
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <div className="rounded-xl bg-black/40 backdrop-blur border border-white/10 p-5 text-center">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">Franchise Slots Remaining</div>
                <div className="font-anton text-5xl text-white mt-1 leading-none">16</div>
                <div className="text-xs text-[#00A86B] mt-1">First-come · First-allocated</div>
              </div>
              <SponsorInquiryDialog defaultIntent="franchise">
                <Button data-testid="public-cta-own-franchise" className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold uppercase tracking-widest h-12 w-full">
                  <Crown className="w-4 h-4 mr-2" /> Apply to Own a Team
                </Button>
              </SponsorInquiryDialog>
              <a href="mailto:owners@marapadel.com" data-testid="public-cta-own-franchise-email" className="text-center text-xs text-zinc-400 hover:text-white">
                or email <span className="underline">owners@marapadel.com</span>
              </a>
            </div>
          </div>
        </div>

        {/* ===== SPONSOR A TEAM ===== */}
        <div id="public-sponsorship" className="mt-8 rounded-2xl overflow-hidden relative border border-[#00A86B]/30" data-testid="public-sponsorship">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1611] via-[#06210f] to-[#006B3F]" />
          <div className="relative p-8 md:p-12 grid lg:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#00A86B] font-bold">
                <Megaphone className="w-3.5 h-3.5" /> Brand Partnerships · Open
              </div>
              <h3 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter text-white mt-2 leading-[0.95]">
                Sponsor a <span className="text-[#00A86B]">Team</span> or the League.
              </h3>
              <p className="mt-4 text-zinc-300 text-[15px] leading-relaxed max-w-2xl">
                Put your brand on the jersey, the court, or the broadcast. Whether you back a single
                franchise or the entire tournament, sponsorship packages are tailored to reach Kenya&apos;s
                most affluent, sports-engaged audience — live and across our digital channels.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-zinc-200">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#00A86B] shrink-0" /> Jersey placements (chest · sleeve · back)</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#00A86B] shrink-0" /> Court & glass-wall branding zones</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#00A86B] shrink-0" /> Title, Gold, Silver & Court sponsor tiers</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#00A86B] shrink-0" /> Stalls + on-site activation slots</li>
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <div className="rounded-xl bg-black/40 backdrop-blur border border-white/10 p-5 text-center">
                <div className="text-[10px] uppercase tracking-widest text-zinc-400">Sponsor Tiers</div>
                <div className="font-anton text-3xl text-white mt-1 leading-none">Title · Gold · Silver · Court</div>
                <div className="text-xs text-[#00A86B] mt-1">Custom packages available</div>
              </div>
              <SponsorInquiryDialog defaultIntent="sponsorship">
                <Button data-testid="public-cta-sponsor-team" className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest h-12 w-full">
                  <Handshake className="w-4 h-4 mr-2" /> Become a Sponsor
                </Button>
              </SponsorInquiryDialog>
              <a href="mailto:partners@marapadel.com" data-testid="public-cta-sponsor-email" className="text-center text-xs text-zinc-400 hover:text-white">
                or email <span className="underline">partners@marapadel.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== LIVE / RESULTS ===================== */}
      <section id="public-live-section" className="max-w-7xl mx-auto px-6 py-12" data-testid="public-live-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <SectionHeader title="Live On Court" empty={live.length === 0} icon={<span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block mr-2" />} />
          <PushSubscribeButton />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {live.map((m) => <LiveTile key={m.id} matchId={m.id} fallback={m} />)}
        </div>

        {finished.length > 0 && (
          <>
            <div className="mt-12 mb-3"><SectionHeader title="Recent Results" icon={<Trophy className="w-4 h-4 text-[#00A86B] inline-block mr-2" />} /></div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {finished.map((m) => (
                <Card key={m.id} className="bg-[#11241b] border-white/10 p-5" data-testid={`finished-${m.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    {m.court} · {m.stage}{m.seed ? ` · Seed ${m.seed}` : ""} · FINAL
                  </div>
                  <PadelFinalScore m={m} />
                </Card>
              ))}
            </div>
          </>
        )}

        {upcoming.length > 0 && (
          <>
            <div className="mt-12 mb-3"><SectionHeader title="Up Next" /></div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {upcoming.map((m) => (
                <Card key={m.id} className="bg-[#11241b] border-white/10 p-4" data-testid={`upcoming-${m.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.court} · {m.stage}</div>
                  <div className="mt-1 flex items-baseline justify-between gap-2">
                    <div className="font-anton text-lg uppercase text-white truncate">{m.team_a?.name}</div>
                    <span className="text-zinc-600 text-xs">vs</span>
                    <div className="font-anton text-lg uppercase text-white text-right truncate">{m.team_b?.name}</div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {live.length === 0 && upcoming.length === 0 && finished.length === 0 && (
          <Card className="p-12 text-center bg-[#11241b] border-white/10" data-testid="public-empty">
            <Tv className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
            <div className="font-anton text-2xl uppercase text-white">No matches scheduled yet</div>
            <div className="text-sm text-zinc-400 mt-1">Check back on tournament dates {data.league_dates}.</div>
          </Card>
        )}

        {data.sponsors.length > 0 && (
          <div className="mt-16 pt-8 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 text-center mb-4">Powered by our sponsors</div>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              {data.sponsors.map((s) => (
                <div key={s.id} className="font-anton text-xl uppercase text-zinc-400 hover:text-white transition-colors" data-testid={`public-sponsor-${s.id}`}>{s.name}</div>
              ))}
            </div>
            <div className="text-center mt-4">
              <SponsorInquiryDialog>
                <button data-testid="public-cta-sponsor-bottom" className="text-xs uppercase tracking-widest text-[#00A86B] hover:text-white">↗ Become a Mara Padel Sponsor</button>
              </SponsorInquiryDialog>
            </div>
          </div>
        )}
      </section>

      <HashtagWall wall={site?.hashtag_wall} />

      <div className="kenya-stripe" />
      <footer className="bg-[#0a1611] py-8 text-center text-xs text-zinc-500">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3"><Logo variant="inline" className="h-8" /></div>
          <div>© {new Date().getFullYear()} Mara Padel League · Season 03 · Kenya</div>
          <div className="flex gap-4">
            <Link to="/register-player" className="hover:text-white">Register</Link>
            <Link to="/admin" className="hover:text-white">Operator</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TopNav() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#060d09]/85 border-b border-white/5">
      <div className="kenya-stripe" />
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Logo variant="inline" className="h-10" />
        <nav className="hidden md:flex items-center gap-6 text-sm uppercase tracking-widest text-zinc-400">
          <a href="#public-about" className="hover:text-white">About</a>
          <a href="#public-live-section" className="hover:text-white">Live</a>
          <a href="#public-own-franchise" className="hover:text-[#E32424]">Own a Team</a>
          <a href="#public-sponsorship" className="hover:text-[#00A86B]">Sponsor</a>
          <Link to="/register-player" className="hover:text-white">Register</Link>
          <Link to="/admin" className="hover:text-[#00A86B]">Operator Login</Link>
        </nav>
      </div>
    </header>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border-l-2 border-[#00A86B] pl-3">
      <div className="font-anton text-3xl md:text-4xl uppercase tracking-tighter text-white leading-none">{n}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">{l}</div>
    </div>
  );
}

function Pillar({ n, icon: Icon, title, body }) {
  return (
    <Card className="card-mara p-5 hover:border-[#00A86B] transition-colors">
      <div className="flex items-baseline justify-between">
        <div className="font-anton text-3xl text-[#00A86B] leading-none">{n}</div>
        <Icon className="w-5 h-5 text-[#E32424]" />
      </div>
      <div className="font-anton text-xl uppercase tracking-tight text-white mt-4 leading-tight">{title}</div>
      <div className="text-sm text-zinc-300 mt-2 leading-relaxed">{body}</div>
    </Card>
  );
}

function SectionHeader({ title, icon, empty }) {
  return (
    <div className="font-anton text-2xl uppercase tracking-tight text-white mb-4">
      {icon}{title} {empty && <span className="text-sm text-zinc-500 normal-case font-sans tracking-normal ml-3">(no live matches right now)</span>}
    </div>
  );
}

function LiveTile({ matchId, fallback }) {
  const { state: m } = useMatchSocket(matchId);
  const data = m || fallback;
  if (!data) return null;
  // Team names come from the REST snapshot (`fallback`) since the WebSocket
  // ships only the raw match doc (no denormalised names).
  const teamAName = fallback?.team_a_name || fallback?.team_a?.name || data?.team_a?.name || "A";
  const teamBName = fallback?.team_b_name || fallback?.team_b?.name || data?.team_b?.name || "B";
  return (
    <Card className="bg-[#11241b] border-white/10 p-5 relative overflow-hidden" data-testid={`public-live-${matchId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          {data.court} · {data.stage}
          {data.seed ? ` · Seed ${data.seed}` : ""}
        </div>
        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-600 text-white flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
        </span>
      </div>
      <PadelLiveScore m={data} teamAName={teamAName} teamBName={teamBName} />
      <Link to={`/watch/${matchId}`} data-testid={`watch-link-${matchId}`}
        className="mt-4 flex items-center justify-center gap-2 w-full py-2 rounded-md bg-[#E32424] hover:bg-[#a01818] text-white text-xs font-bold uppercase tracking-widest transition-colors">
        <Tv className="w-4 h-4" /> Watch Live
      </Link>
    </Card>
  );
}
